import * as THREE from 'three';
import type { Vec3 } from '@vrlearning/lesson-schema';
import type { FeatureBadgeEntry } from '../engine/feature-badges.js';
import { LABEL_TEXT } from '../engine/text.js';
import { makeLabel } from './panel.js';
import { PANO_SPOT } from './pano.js';
import type { BuiltObject } from './scene.js';

/** A direction hotspot in a 360° photo (ADR-036): its key, its title, and where it points in the room. */
export type DirectionEntry = { key: string; title: string; direction: Vec3 };

/** How much bigger than an object's name a direction's label is: 1.8° letters at 7 m. */
const DIRECTION_LABEL = 1.5;
/** A direction badge's title shows on up to two lines (docs/04, Direction hotspots). */
export const DIRECTION_LABEL_LINES: number = LABEL_TEXT.directionLines;
const unitPlane = new THREE.PlaneGeometry(1, 1);
/**
 * A feature's ⓘ (ADR-087) is drawn without the depth test, so over every opaque world mesh (three.js
 * draws those first): a feature's round only approximates its mesh, which would otherwise hide the
 * badge on it. Its render order is below every transparent lectern and UI surface (the panel at 0,
 * buttons, plate, locks, handles, teleport marker), which so draw after it and hide it, wherever the
 * head moves: docs/04, the panel is never covered.
 */
export const FEATURE_BADGE_ORDER = -0.5;

/**
 * Floating ⓘ badges above objects that have something to say on this step.
 *
 * docs/04-lesson-format.md: "floating ⓘ badges above the object, always facing
 * the viewer". Sprites face the camera for free. The badge pulses gently so it
 * reads as something to point at rather than scene decoration.
 *
 * The solar-system prototype used torus rings around the object instead; the
 * documented rule is the badge, and it survives objects of very different
 * sizes (the Sun is 2.4 m, the Moon 0.15 m) where a proportional ring does not.
 */
export class HotspotBadges {
  readonly group = new THREE.Group();
  #texture: THREE.Texture | undefined;
  #targets: { sprite: THREE.Sprite; object: BuiltObject }[] = [];
  readonly #directionGroup = new THREE.Group();
  #directions: {
    key: string;
    mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
    label: THREE.Sprite;
    direction: THREE.Vector3;
  }[] = [];
  readonly #featureGroup = new THREE.Group();
  #features: {
    key: string;
    mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
    size: number;
  }[] = [];
  /** Placed on the first frame the eye is known after they were asked for. */
  #unplaced = false;
  readonly #eye = new THREE.Vector3();
  readonly #raycaster = new THREE.Raycaster();

  /** @param texture The ⓘ's texture; drawn on a canvas unless given (tests). */
  constructor(
    parent: THREE.Object3D,
    readonly texture?: () => THREE.Texture,
  ) {
    parent.add(this.group);
    this.group.add(this.#directionGroup, this.#featureGroup);
  }

  #badgeTexture(): THREE.Texture {
    if (this.#texture) return this.#texture;
    if (this.texture) {
      this.#texture = this.texture();
      return this.#texture;
    }

    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#8fd3ff';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 5;
    ctx.stroke();

    ctx.fillStyle = '#0b1120';
    ctx.font = `bold ${size * 0.62}px Georgia, serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('i', size / 2, size / 2 + 2);

    this.#texture = new THREE.CanvasTexture(canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    return this.#texture;
  }

  /** Replaces the badges with one per distinct object named by the current step. */
  show(objects: BuiltObject[]): void {
    this.#clearObjects();
    // Several hotspots may name one object: it still gets one badge.
    for (const object of new Set(objects)) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.#badgeTexture(),
          transparent: true,
          depthWrite: false,
        }),
      );
      // Big enough to point at from a few metres, small enough not to hide the
      // object it belongs to.
      sprite.scale.setScalar(badgeSize(object));
      this.group.add(sprite);
      this.#targets.push({ sprite, object });
    }
  }

  /** Every badge: the objects', the directions' and the features'. */
  clear(): void {
    this.#clearObjects();
    this.#clearDirections();
    this.clearFeatures();
  }

  #clearObjects(): void {
    for (const { sprite } of this.#targets) {
      sprite.material.dispose();
      sprite.removeFromParent();
    }
    this.#targets = [];
  }

  /**
   * Direction hotspots in a 360° photo (ADR-036): one badge per entry, 7 m out
   * from the pupil's eye along its direction, 0.4 m across (3.3°), with the
   * hotspot's title under it. The badge itself is what a ray opens. Placed on
   * the first `update` that brings the eye (after the step's arrival, the rig
   * moved), and left there: the photo is at infinity, a head's move is not.
   */
  showDirections(entries: readonly DirectionEntry[]): void {
    this.#clearDirections();
    for (const entry of entries) {
      const mesh = new THREE.Mesh(
        unitPlane,
        new THREE.MeshBasicMaterial({
          map: this.#badgeTexture(),
          transparent: true,
          depthWrite: false,
          fog: false,
        }),
      );
      mesh.name = `richting:${entry.key}`;
      mesh.userData['direction'] = entry.key;
      // Its title whole, on up to two lines (a returning point's '<punt> — <vraag>', docs/04).
      const label = makeLabel(entry.title, DIRECTION_LABEL, DIRECTION_LABEL_LINES);
      this.#directionGroup.add(mesh, label);
      this.#directions.push({
        key: entry.key,
        mesh,
        label,
        direction: new THREE.Vector3(...entry.direction).normalize(),
      });
    }
    this.#unplaced = entries.length > 0;
  }

  /** Placed again from the eye on the next `update` (the eyes' height has just become known). */
  placeDirectionsAgain(): void {
    if (this.#directions.length > 0) this.#unplaced = true;
  }

  /** The direction badges a ray can open. */
  get directionTargets(): THREE.Mesh[] {
    return this.#directions.map(({ mesh }) => mesh);
  }

  /** The direction badge a ray lands on, if one is shown. */
  directionAlong(ray: THREE.Ray): { key: string; distance: number } | undefined {
    if (!this.#directionGroup.visible || this.#unplaced || this.#directions.length === 0) {
      return undefined;
    }
    this.#raycaster.set(ray.origin, ray.direction);
    const hit = this.#raycaster.intersectObjects(this.directionTargets, false)[0];
    const key = hit?.object.userData['direction'] as string | undefined;
    return hit && key ? { key, distance: hit.distance } : undefined;
  }

  #clearDirections(): void {
    for (const { mesh, label } of this.#directions) {
      mesh.material.dispose();
      label.material.map?.dispose();
      label.material.dispose();
    }
    this.#directionGroup.clear();
    this.#directions = [];
    this.#unplaced = false;
  }

  /**
   * Keeps each badge above its object, which may be orbiting; and the
   * direction badges, while `directions` says the step's photo is shown, at
   * their place from the eye, turned to it.
   */
  update(
    elapsed: number,
    waiting: (object: BuiltObject) => boolean = () => false,
    directions?: { eye: THREE.Vector3; shown: boolean },
  ): void {
    this.#updateDirections(elapsed, directions);
    const position = new THREE.Vector3();
    for (const { sprite, object } of this.#targets) {
      // Not before its object is there: a step that shows it grows it in first.
      sprite.visible = !waiting(object);
      if (object.badge) {
        // Just above its name, wherever the builder put that. The planet rule
        // below put a bone's badge 1.34 m above it: a bone has no radius, so
        // it got the planet default of half a metre.
        // Straight above, however a step has turned the object (render/motion.ts
        // keeps its name upright the same way); a scaled object's top is higher.
        const upright = object.badge.at
          .clone()
          .applyQuaternion(object.group.quaternion.clone().invert());
        sprite.position.copy(object.group.localToWorld(upright));
      } else {
        object.body.getWorldPosition(position);
        sprite.position.copy(position);
        // Clear of the object's name label, which sits at radius * 1.35 + 0.2.
        sprite.position.y += object.radius * 1.35 + 0.66;
      }
      const pulse = 1 + Math.sin(elapsed * 3) * 0.06;
      sprite.scale.setScalar(badgeSize(object) * pulse);
    }
  }

  #updateDirections(elapsed: number, directions?: { eye: THREE.Vector3; shown: boolean }): void {
    const shown = directions?.shown === true && this.#directions.length > 0;
    this.#directionGroup.visible = shown;
    if (!shown || !directions) return;
    if (this.#unplaced) {
      this.#eye.copy(directions.eye);
      for (const { mesh, label, direction } of this.#directions) {
        mesh.position.copy(this.#eye).addScaledVector(direction, PANO_SPOT.distance);
        label.position.copy(mesh.position);
        label.position.y -= PANO_SPOT.size / 2 + label.scale.y / 2 + 0.025;
      }
      this.#unplaced = false;
    }
    const pulse = 1 + Math.sin(elapsed * 3) * 0.06;
    for (const { mesh } of this.#directions) {
      mesh.lookAt(directions.eye);
      mesh.scale.setScalar(PANO_SPOT.size * pulse);
      mesh.updateMatrixWorld();
    }
  }

  /**
   * World features' badges (ADR-087): one 1 × 1 plane per entry, at its place in the room and drawn
   * `size` wide, over the world and under the lectern (`FEATURE_BADGE_ORDER`). Placed by the caller with the lectern
   * (engine/feature-badges.ts); `updateFeatures` turns them to the eye and pulses them. The badge
   * itself is what a ray opens: a feature is no pickable mesh.
   */
  showFeatures(entries: readonly FeatureBadgeEntry[]): void {
    this.clearFeatures();
    for (const entry of entries) {
      const mesh = new THREE.Mesh(
        unitPlane,
        new THREE.MeshBasicMaterial({
          map: this.#badgeTexture(),
          transparent: true,
          depthWrite: false,
          depthTest: false,
          fog: false,
        }),
      );
      mesh.name = entry.key;
      mesh.userData['feature'] = entry.key;
      mesh.renderOrder = FEATURE_BADGE_ORDER;
      mesh.position.set(...entry.at);
      mesh.scale.setScalar(entry.size);
      mesh.updateMatrixWorld();
      this.#featureGroup.add(mesh);
      this.#features.push({ key: entry.key, mesh, size: entry.size });
    }
  }

  clearFeatures(): void {
    for (const { mesh } of this.#features) mesh.material.dispose();
    this.#featureGroup.clear();
    this.#features = [];
  }

  /** Turns each feature badge to the eye and pulses it, as the objects' badges pulse. */
  updateFeatures(elapsed: number, eye: THREE.Vector3): void {
    const pulse = 1 + Math.sin(elapsed * 3) * 0.06;
    for (const { mesh, size } of this.#features) {
      mesh.lookAt(eye);
      mesh.scale.setScalar(size * pulse);
      mesh.updateMatrixWorld();
    }
  }

  /** The feature badges a ray can open. */
  get featureTargets(): THREE.Mesh[] {
    return this.#features.map(({ mesh }) => mesh);
  }

  /**
   * The feature badge a ray lands on, if one is shown and no `blocker` (the lectern's panel, the
   * subtitle plate: surfaces drawn over the badge) is nearer on the ray: a badge hidden behind the
   * panel is not pressed through it. Blockers are passed shown; a hidden one must not be passed.
   */
  featureAlong(
    ray: THREE.Ray,
    blockers: readonly THREE.Object3D[] = [],
  ): { key: string; distance: number } | undefined {
    if (this.#features.length === 0) return undefined;
    this.#raycaster.set(ray.origin, ray.direction);
    const hit = this.#raycaster.intersectObjects(this.featureTargets, false)[0];
    const key = hit?.object.userData['feature'] as string | undefined;
    if (!hit || !key) return undefined;
    const block =
      blockers.length > 0 ? this.#raycaster.intersectObjects([...blockers], false)[0] : undefined;
    if (block && block.distance < hit.distance) return undefined;
    return { key, distance: hit.distance };
  }

  dispose(): void {
    this.clear();
    this.#texture?.dispose();
    // The stage outlives a lesson, so this has to leave the scene itself.
    this.group.removeFromParent();
  }
}

/**
 * Big enough to point at from a few metres, small enough not to hide the
 * object it belongs to. A planet's scales with the planet; a bone's is set by
 * its builder, beside its name.
 */
function badgeSize(object: BuiltObject): number {
  return object.badge?.size ?? Math.max(0.12, Math.min(0.34, object.radius * 0.45));
}
